import { REPLACE_STATE_EVENT } from './constants';
import { Path, Location, parsePath } from 'react-router-dom';

/**
 * `window.history.replaceState` doesn't trigger `popstate` event or any other event.
 *  So we need to dispatch a custom event to notify other components.
 * */
const dispatchReplaceStateEvent = (url: string): void => {
  window.dispatchEvent(
    new CustomEvent(REPLACE_STATE_EVENT, {
      detail: { url },
    })
  );
};

/**
 * Parse an absolute http(s) URL. Returns `null` for invalid or non-http(s) input.
 */
export const parseHttpUrl = (str: string): URL | null => {
  try {
    const url = new URL(str);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return url;
  } catch {
    return null;
  }
};

export const isUrl = (str: string): boolean => parseHttpUrl(str) !== null;

export const setQueryParamSilently = (
  key: string,
  value: string | number
): void => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, String(value));
  window.history.replaceState(
    window.history.state,
    window.document.title,
    url.toString()
  );
  dispatchReplaceStateEvent(url.toString());
};

export const setSerializedQueryParamSilently = (
  key: string,
  value: any
): void => {
  try {
    const serializedValue = encodeURIComponent(JSON.stringify(value));
    setQueryParamSilently(key, serializedValue);
  } catch {
    console.error('Error serializing value');
  }
};

export const clearQueryParamSilently = (keys: string | string[]): void => {
  if (!Array.isArray(keys)) keys = [keys];
  const url = new URL(window.location.href);

  let changed = false;
  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (!changed) return;

  window.history.replaceState(
    window.history.state,
    window.document.title,
    url.toString()
  );
  dispatchReplaceStateEvent(url.toString());
};

export const getQueryParam = (key: string): string | null => {
  return new URLSearchParams(window.location.search).get(key);
};

export const getDeserializedQueryParam = <T = any>(
  key: string
): T | undefined => {
  const value = new URLSearchParams(window.location.search).get(key);
  if (value) {
    try {
      return JSON.parse(decodeURIComponent(value));
    } catch {
      console.error(`Failed to deserialize query param: ${key}`);
    }
  }
  return undefined;
};

export type GetLocationDescriptorSettings = {
  preserveAll?: boolean;
  preserveSearch?: boolean;
  preserveState?: boolean;
  preserveHash?: boolean;
  preserveKey?: boolean;
};

export const getLocationDescriptor = (
  path: Partial<Path> | string,
  location: Location,
  settings?: GetLocationDescriptorSettings
): Partial<Location> => {
  const parsedPath = typeof path === 'string' ? parsePath(path) : path;
  if (
    !settings ||
    (!settings.preserveAll &&
      !settings.preserveSearch &&
      !settings.preserveState &&
      !settings.preserveHash &&
      !settings.preserveKey)
  ) {
    return parsedPath;
  }
  const getSetting = (
    key: keyof GetLocationDescriptorSettings
  ): boolean | undefined =>
    (settings.preserveAll && settings[key] !== false) || settings[key];

  const preserveSearch = getSetting('preserveSearch');
  const preserveState = getSetting('preserveState');
  const preserveHash = getSetting('preserveHash');
  const preserveKey = getSetting('preserveKey');
  const newDestination: Partial<Location> = {
    pathname: parsedPath.pathname,
  };

  if (preserveSearch) {
    const existingSearch = new URLSearchParams(location.search);
    const newSearch = new URLSearchParams(parsedPath.search);
    existingSearch.forEach((value, key) => {
      newSearch.append(key, value);
    });
    const searchString = newSearch.toString();
    if (searchString?.length) {
      newDestination.search = searchString;
    }
  } else if (parsedPath.search) {
    newDestination.search = parsedPath.search;
  }

  if (preserveHash && !parsedPath.hash && location.hash) {
    newDestination.hash = location.hash;
  } else if (parsedPath.hash) {
    newDestination.hash = parsedPath.hash;
  }

  if (preserveState && location.state) {
    newDestination.state = location.state;
  }

  if (preserveKey && location.key) {
    newDestination.key = location.key;
  }

  return newDestination;
};
